import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { normalizeNote, moyenneNormalisee } from '../utils/normalizeNote';
import { openBlobInNewTab, downloadBlob, base64ToBlob } from '../utils/fileUtils';
import { useDocumentPreview } from '../components/DocumentPreviewModal';
import { PhotoIdentite } from '../components/PhotoIdentite';
import { 
  User, FileText, Calendar, Award, TrendingUp, Clock, AlertTriangle, 
  CheckCircle, XCircle, Download, ArrowLeft, MessageSquare, Building2, Phone, Mail, UserCheck, FolderArchive, BookOpen, Eye, Trash2,
  GraduationCap, Loader2, Euro, FileSignature, Send, Save, Edit, Shield, ClipboardList, Bell, Brain, RefreshCw, CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, BarChart, Bar, Cell } from 'recharts';
import CarteEtudiantModal from '../components/CarteEtudiantModal';
import CompetencesValidees from '../components/CompetencesValidees';
import CarnetNotesFormateur from '../components/CarnetNotesFormateur';
import CarnetNotesTuteur from '../components/CarnetNotesTuteur';
import EPMSPConsultation from '../components/EPMSPConsultation';
import CompetencesPrerequisConsultation from '../components/CompetencesPrerequisConsultation';
import ConventionStageSection from '../components/fiche-stagiaire/ConventionStageSection';
import ConventionFormationSection from '../components/fiche-stagiaire/ConventionFormationSection';
import ReglementInterieurSection from '../components/fiche-stagiaire/ReglementInterieurSection';
import DossierCandidatSection from '../components/fiche-stagiaire/DossierCandidatSection';
import StagiaireDocumentsSection from '../components/fiche-stagiaire/StagiaireDocumentsSection';
import BilansMensuelsSection from '../components/fiche-stagiaire/BilansMensuelsSection';
import GrilleEvaluationCentreSection from '../components/fiche-stagiaire/GrilleEvaluationCentreSection';
import HistoriqueModificationsSection from '../components/fiche-stagiaire/HistoriqueModificationsSection';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const FicheStagiaireCentre = ({ user, readOnly = false, backUrl = '/liste-stagiaires' }) => {
  const { previewDocument, DocumentModal } = useDocumentPreview();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const stagiaireId = searchParams.get('id');
  
  const [stagiaire, setStagiaire] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [presences, setPresences] = useState([]);
  const [notes, setNotes] = useState([]);
  const [zipLoading, setZipLoading] = useState(false);
  const [competences, setCompetences] = useState([]);
  const [validationsAutonomie, setValidationsAutonomie] = useState([]);
  const [appreciations, setAppreciations] = useState([]);
  const [heuresParCategorie, setHeuresParCategorie] = useState({});
  const [bilansEntreprise, setBilansEntreprise] = useState([]);
  const [tuteurInfo, setTuteurInfo] = useState(null);
  const [tuteurDocs, setTuteurDocs] = useState([]);
  const [entrepriseInfo, setEntrepriseInfo] = useState(null);
  const [entrepriseDocs, setEntrepriseDocs] = useState([]);
  const [formateurInfo, setFormateurInfo] = useState(null);
  const [formateurDocs, setFormateurDocs] = useState([]);
  const [activeCarnet, setActiveCarnet] = useState('formateur');
  const [autonomiesValidees, setAutonomiesValidees] = useState([]);
  const [autonomieHistorique, setAutonomieHistorique] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageDialog, setMessageDialog] = useState({ open: false, userId: '', userName: '', role: '' });
  const [carteOpen, setCarteOpen] = useState(false);
  const [centreProfile, setCentreProfile] = useState(null);
  const [messageForm, setMessageForm] = useState({ sujet: '', contenu: '', fichier: null });
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Filtres pour la progression des notes
  const [filterCategorie, setFilterCategorie] = useState('');
  const [filterArticle, setFilterArticle] = useState('');
  
  // États pour la bascule en Moniteur
  const [showGraduateModal, setShowGraduateModal] = useState(false);
  const [eligibilityData, setEligibilityData] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [archiveDownloaded, setArchiveDownloaded] = useState(false);
  const [converting, setConverting] = useState(false);
  const [bcValidated, setBcValidated] = useState(false);
  
  // Liaison prospect CRM
  const [prospectLink, setProspectLink] = useState(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [prospectSearch, setProspectSearch] = useState('');
  const [prospectResults, setProspectResults] = useState([]);
  const [searchingProspect, setSearchingProspect] = useState(false);

  // Dossier candidat (historique)
  const [dossierCandidat, setDossierCandidat] = useState(null);

  // Grille d'évaluation
  const [grilleEval, setGrilleEval] = useState(null);
  const [grilleStructure, setGrilleStructure] = useState(null);

  // Convention de Mise en Situation Pédagogique
  const [convStage, setConvStage] = useState({});

  // Autres conventions
  const [autresConventions, setAutresConventions] = useState([]);

  // Devis CRM
  const [devisData, setDevisData] = useState(null);
  const [devisProspectId, setDevisProspectId] = useState(null);
  const [numeroCandidatCRM, setNumeroCandidatCRM] = useState(null);
  const [sendingDevis, setSendingDevis] = useState(false);

  // Documents candidat (pour grille stagiaire)
  const [candidatDocuments, setCandidatDocuments] = useState([]);

  // Paiements candidat
  const [candidatPaiements, setCandidatPaiements] = useState([]);

  // Analyse IA
  const [analyseIA, setAnalyseIA] = useState(null);
  const [analyseIALoading, setAnalyseIALoading] = useState(false);

  // Documents requis pour un stagiaire - identique à StagiaireDocumentsSection
  const documentsRequis = [
    { type: 'cni_passeport', label: "Carte d'identité / Passeport", required: true },
    { type: 'psc', label: 'PSC', required: true },
    { type: 'certificat_medical', label: 'Certificat médical', required: true },
    { type: 'photo_identite', label: "Photo d'identité (format JPG)", required: true },
    { type: 'attestation_natation', label: 'Attestation natation 100m', required: true },
    { type: 'attestation_recensement', label: 'Attestation de recensement', required: false },
    { type: 'permis_cotier', label: 'Permis côtier', required: true },
    { type: 'permis_fluvial', label: 'Permis fluvial', required: true },
    { type: 'crr', label: 'CRR', required: false },
    { type: 'signature', label: 'Signature', required: true },
    { type: 'cv', label: 'CV', required: true },
    { type: 'attestation_epmsp', label: 'Attestation EPMSP', required: false },
    { type: 'declaration_epmsp', label: 'Déclaration stagiaire', required: false },
  ];

  useEffect(() => {
    const controller = new AbortController();
    if (stagiaireId) {
      loadAllData(controller.signal);
    }
    return () => controller.abort();
  }, [stagiaireId]);

  const loadAllData = async (signal) => {
    setLoading(true);
    try {
      // ENDPOINT AGRÉGÉ: 1 seule requête au lieu de ~25
      const res = await axios.get(`/stagiaires/${stagiaireId}/full`, { signal });
      const data = res.data;
      
      // Mapper toutes les données
      setStagiaire(data.stagiaire);
      setDocuments(data.documents || []);
      setPresences(data.presences || []);
      setNotes(data.notes || []);
      setCompetences(data.competences || []);
      setValidationsAutonomie(data.validations_autonomie || []);
      setAppreciations(data.appreciations || []);
      setBilansEntreprise(data.bilans_entreprise || []);
      
      // Autonomies
      const autoEnAttente = data.autonomies_en_attente || [];
      const autonomiesAcceptees = autoEnAttente.filter(a => a.stagiaire_reponse === true);
      setAutonomiesValidees(autonomiesAcceptees);
      setAutonomieHistorique(data.autonomie_historique || []);

      // Heures encadrement
      const heuresData = data.heures_encadrement || [];
      if (Array.isArray(heuresData)) {
        const cumul = {};
        heuresData.forEach(h => {
          const cat = h.categorie || 'Autre';
          const sousCat = h.sous_categorie || 'Général';
          if (!cumul[cat]) cumul[cat] = { total: 0, sousCat: {} };
          cumul[cat].total += h.heures || 0;
          if (!cumul[cat].sousCat[sousCat]) cumul[cat].sousCat[sousCat] = 0;
          cumul[cat].sousCat[sousCat] += h.heures || 0;
        });
        setHeuresParCategorie(cumul);
      }

      // Tuteur, Entreprise, Formateur
      if (data.tuteur_info) setTuteurInfo(data.tuteur_info);
      setTuteurDocs(data.tuteur_docs || []);
      if (data.entreprise_info) setEntrepriseInfo(data.entreprise_info);
      setEntrepriseDocs(data.entreprise_docs || []);
      if (data.formateur_info) setFormateurInfo(data.formateur_info);
      setFormateurDocs(data.formateur_docs || []);
      
      // Eligibility BC
      if (data.eligibility?.eligible) {
        setBcValidated(true);
        setEligibilityData(data.eligibility);
      }
      
      // Prospect link
      if (data.prospect_link?.linked) {
        setProspectLink(data.prospect_link);
      }
      
      // Convention stage
      setConvStage(data.convention_stage || {});

      // Dossier candidat
      if (data.dossier_candidat?.has_data) {
        setDossierCandidat(data.dossier_candidat);
        setCandidatPaiements(data.dossier_candidat.paiements || []);
      }

      // Autres conventions
      setAutresConventions(data.autres_conventions || []);

      // Documents candidat
      setCandidatDocuments(data.candidat_documents || []);

      // Grille evaluation
      if (data.grille_evaluation) setGrilleEval(data.grille_evaluation);
      if (data.grille_structure) setGrilleStructure(data.grille_structure);

      // Devis CRM
      if (data.devis) {
        setDevisData(data.devis);
        setDevisProspectId(data.devis_prospect_id);
      }
      if (data.numero_candidat) {
        setNumeroCandidatCRM(data.numero_candidat);
      }

      // Analyse IA
      if (data.analyse_ia?.analyse) {
        setAnalyseIA(data.analyse_ia);
      }

      // Charger le profil du centre pour la carte etudiant
      try {
        const centreRes = await axios.get('/centres-profiles', { signal });
        const centreName = data.stagiaire?.centre_formation;
        if (centreName && centreRes.data?.[centreName]) {
          setCentreProfile(centreRes.data[centreName]);
        }
      } catch (e) {
        // Non bloquant
      }
    } catch (error) {
      if (!axios.isCancel(error)) toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  // Vérifier les documents manquants (fusionner documents stagiaire + candidat)
  const getDocumentsManquants = () => {
    const allDocs = [...(documents || []), ...(candidatDocuments || [])];
    const uploaded = allDocs.map(d => d.document_type || d.type_document || d.type);
    return documentsRequis.filter(dr => !uploaded.includes(dr.type));
  };

  // Recherche de prospects CRM
  const handleSearchProspect = async (query) => {
    setProspectSearch(query);
    setSearchingProspect(true);
    try {
      const url = query.length >= 2 ? `/prospects/search?q=${encodeURIComponent(query)}` : '/prospects/search';
      const res = await axios.get(url);
      setProspectResults(res.data);
    } catch (e) {
      setProspectResults([]);
    } finally {
      setSearchingProspect(false);
    }
  };

  // Charger tous les prospects à l'ouverture du modal
  const openLinkModal = async () => {
    setShowLinkModal(true);
    setSearchingProspect(true);
    try {
      const res = await axios.get('/prospects/search');
      setProspectResults(res.data);
    } catch (e) {
      setProspectResults([]);
    } finally {
      setSearchingProspect(false);
    }
  };

  const handleLinkProspect = async (prospectId) => {
    try {
      await axios.post(`/candidats/${stagiaireId}/prospect-link`, { prospect_id: prospectId });
      toast.success('Prospect lié avec succès');
      setShowLinkModal(false);
      setProspectSearch('');
      setProspectResults([]);
      // Recharger la liaison
      const linkRes = await axios.get(`/candidats/${stagiaireId}/prospect-link`);
      setProspectLink(linkRes.data.linked ? linkRes.data : null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la liaison');
    }
  };

  const handleUnlinkProspect = async () => {
    if (!window.confirm('Supprimer la liaison avec ce prospect ?')) return;
    try {
      await axios.delete(`/candidats/${stagiaireId}/prospect-link`);
      toast.success('Liaison supprimée');
      setProspectLink(null);
    } catch (error) {
      toast.error('Erreur lors de la suppression de la liaison');
    }
  };

  // Calculer les absences et retards avec distinction Centre / Entreprise
  const getAbsencesRetards = () => {
    const absencesCentre = presences.filter(p => p.statut === 'absent' && p.type_presence !== 'entreprise').length;
    const retardsCentre = presences.filter(p => p.statut === 'retard' && p.type_presence !== 'entreprise').length;
    const absencesEntreprise = presences.filter(p => p.statut === 'absent' && p.type_presence === 'entreprise').length;
    const retardsEntreprise = presences.filter(p => p.statut === 'retard' && p.type_presence === 'entreprise').length;
    const totalAbsences = absencesCentre + absencesEntreprise;
    const totalRetards = retardsCentre + retardsEntreprise;
    return { absencesCentre, retardsCentre, absencesEntreprise, retardsEntreprise, totalAbsences, totalRetards };
  };

  // Obtenir les catégories et articles uniques des notes
  const getUniqueCategories = () => {
    return [...new Set(notes.map(n => n.categorie))].filter(Boolean).sort();
  };
  
  const getUniqueArticles = () => {
    let filteredNotes = notes;
    if (filterCategorie) {
      filteredNotes = notes.filter(n => n.categorie === filterCategorie);
    }
    return [...new Set(filteredNotes.map(n => n.item))].filter(Boolean).sort();
  };

  // Préparer les données pour le graphique des notes (avec filtres)
  const prepareNotesChartData = () => {
    // Appliquer les filtres
    let filteredNotes = notes;
    if (filterCategorie) {
      filteredNotes = filteredNotes.filter(n => n.categorie === filterCategorie);
    }
    if (filterArticle) {
      filteredNotes = filteredNotes.filter(n => n.item === filterArticle);
    }
    
    const grouped = filteredNotes.reduce((acc, note) => {
      const date = note.date?.split('T')[0] || '';
      if (!date) return acc;
      if (!acc[date]) acc[date] = { vals: [], details: [] };
      acc[date].vals.push(normalizeNote(note));
      acc[date].details.push({
        categorie: note.categorie || 'Autre',
        item: note.item || '',
        note: normalizeNote(note),
        commentaire: note.commentaire || '',
        formateur: note.formateur_nom || ''
      });
      return acc;
    }, {});

    return Object.entries(grouped)
      .map(([date, data]) => ({
        date: new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }),
        fullDate: date,
        note: parseFloat((data.vals.reduce((a, b) => a + b, 0) / data.vals.length).toFixed(1)),
        details: data.details
      }))
      .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));
  };

  // Grouper notes par catégorie
  const notesByCategorie = notes.reduce((acc, note) => {
    if (!acc[note.categorie]) acc[note.categorie] = [];
    acc[note.categorie].push(note);
    return acc;
  }, {});

  // Compétences avec double validation
  const getCompetencesStatus = () => {
    const status = {};
    competences.forEach(c => {
      if (c.sous_categorie !== 'Validation du Support') {
        const key = `${c.categorie}__${c.sous_categorie}`;
        status[key] = {
          ...c,
          tuteur_valide: c.statut === 'competent',
          entreprise_valide: validationsAutonomie.some(
            v => v.categorie === c.categorie && v.sous_categorie === c.sous_categorie && v.valide
          )
        };
      }
    });
    return status;
  };

  const viewDocument = async (documentId, filename) => {
    await previewDocument(documentId, filename);
  };

  // Télécharger un document
  const handleSendMessage = async () => {
    if (!messageForm.contenu.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }
    setSendingMessage(true);
    try {
      // Envoyer le message texte
      await axios.post('/messages/send', {
        to_user_id: messageDialog.userId,
        sujet: messageForm.sujet,
        contenu: messageForm.contenu
      });

      // Si un fichier est joint, l'uploader aussi
      if (messageForm.fichier) {
        const formData = new FormData();
        formData.append('file', messageForm.fichier);
        formData.append('document_type', 'message_document');
        formData.append('user_id', messageDialog.userId);
        await axios.post(`/upload/${messageDialog.userId}?document_type=message_document`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }

      toast.success(`Message envoyé à ${messageDialog.userName}`);
      setMessageDialog({ open: false, userId: '', userName: '', role: '' });
      setMessageForm({ sujet: '', contenu: '', fichier: null });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur envoi message');
    } finally {
      setSendingMessage(false);
    }
  };


  const downloadDocument = async (documentId, filename) => {
    try {
      // Téléchargement direct via URL streaming (pas de base64 JSON)
      const token = localStorage.getItem('token');
      const baseUrl = axios.defaults.baseURL || '';
      const link = document.createElement('a');
      link.href = `${baseUrl}/documents/file/${documentId}?token=${token}`;
      link.setAttribute('download', filename || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Supprimer un document
  const deleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ? Cette action supprimera également le document de l\'espace personnel de l\'utilisateur.')) {
      return;
    }
    try {
      await axios.delete(`/documents/${documentId}`);
      toast.success('Document supprimé');
      // Recharger tous les documents
      const docsRes = await axios.get(`/documents/${stagiaireId}`).catch(() => ({ data: [] }));
      setDocuments(docsRes.data);
      if (stagiaire?.tuteur_id) {
        const tutDocsRes = await axios.get(`/documents/${stagiaire.tuteur_id}`).catch(() => ({ data: [] }));
        setTuteurDocs(tutDocsRes.data);
      }
      if (stagiaire?.entreprise_id) {
        const entDocsRes = await axios.get(`/documents/${stagiaire.entreprise_id}`).catch(() => ({ data: [] }));
        setEntrepriseDocs(entDocsRes.data);
      }
      if (stagiaire?.formateur_id) {
        const frmDocsRes = await axios.get(`/documents/${stagiaire.formateur_id}`).catch(() => ({ data: [] }));
        setFormateurDocs(frmDocsRes.data);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // === Autres conventions (lecture seule) ===
  const handleViewAutreConvention = async (docId) => {
    try {
      const response = await axios.get(`/candidats/${stagiaireId}/autres-conventions/${docId}/view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error("Erreur lors de l'ouverture du document");
    }
  };

  const handleDownloadAutreConvention = async (docId, filename) => {
    try {
      const response = await axios.get(`/candidats/${stagiaireId}/autres-conventions/${docId}/view`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || `${stagiaire?.user_info?.nom || 'stagiaire'}_${stagiaire?.user_info?.prenom || ''}_document.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  // === Devis handlers ===
  const handleViewDevis = async () => {
    if (!devisData || !devisProspectId) return;
    try {
      const response = await axios.get(`/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      openBlobInNewTab(blob);
    } catch (error) {
      toast.error("Erreur lors de la prévisualisation");
    }
  };

  const handleDownloadDevis = async () => {
    if (!devisData || !devisProspectId) return;
    try {
      const response = await axios.get(`/crm/prospects/${devisProspectId}/devis/${devisData.devis_id}/download`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Devis_${numeroCandidatCRM || ''}_${stagiaire?.user_info?.nom}_${stagiaire?.user_info?.prenom}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleSendDevis = async () => {
    if (!devisData || !stagiaireId) return;
    setSendingDevis(true);
    try {
      await axios.post(`/candidats/${stagiaireId}/devis/send`);
      toast.success("Devis envoyé au stagiaire pour signature");
      loadAllData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de l'envoi");
    } finally {
      setSendingDevis(false);
    }
  };

  // Télécharger le carnet de notes en PDF
  const downloadCarnetNotes = async () => {
    try {
      const response = await axios.get(`/pdf/carnet-notes/${stagiaireId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stagiaire?.user_info?.nom || 'stagiaire'}_${stagiaire?.user_info?.prenom || ''}_carnet_notes.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Carnet de notes téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Télécharger la feuille de présence
  const downloadFeuillePresence = async () => {
    try {
      const response = await axios.get(`/pdf/feuille-presence/${stagiaireId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stagiaire?.user_info?.nom || 'stagiaire'}_${stagiaire?.user_info?.prenom || ''}_presence.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feuille de présence téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const downloadAllDocumentsZip = async () => {
    setZipLoading(true);
    try {
      toast.info('Préparation du dossier complet...');
      const response = await axios.get(`/candidats/${stagiaireId}/dossier-complet-zip`, { responseType: 'blob', timeout: 180000 });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dossier_complet_${stagiaire?.user_info?.nom || ''}_${stagiaire?.user_info?.prenom || ''}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Dossier complet téléchargé');
    } catch (error) {
      toast.error('Erreur lors de la préparation du dossier complet');
    } finally {
      setZipLoading(false);
    }
  };

  // === Fonctions pour la bascule en Moniteur VNM ===
  const checkGraduateEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const response = await axios.get(`/moniteur/check-eligibility/${stagiaireId}`);
      setEligibilityData(response.data);
      setShowGraduateModal(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la vérification');
    } finally {
      setCheckingEligibility(false);
    }
  };

  const downloadGraduateArchive = async () => {
    try {
      toast.info('Génération de l\'archive complète...');
      const response = await axios.get(`/moniteur/generate-archive/${stagiaireId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dossier_fin_formation_${stagiaire?.user_info?.nom || ''}_${stagiaire?.user_info?.prenom || ''}_${new Date().toISOString().split('T')[0]}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setArchiveDownloaded(true);
      toast.success('Archive téléchargée avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la génération de l\'archive');
    }
  };

  const documentsManquants = getDocumentsManquants();
  const { absencesCentre, retardsCentre, absencesEntreprise, retardsEntreprise, totalAbsences, totalRetards } = getAbsencesRetards();
  const moyenneGenerale = notes.length > 0 
    ? moyenneNormalisee(notes).toFixed(1)
    : 0;
  const competencesStatus = getCompetencesStatus();

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  if (!stagiaire) {
    return (
      <div className="text-center py-12">
        <p className="text-slate-500 mb-4">Stagiaire non trouvé</p>
        <Link to={backUrl}>
          <Button variant="outline">Retour à la liste</Button>
        </Link>
      </div>
    );
  }

  // Mettre à jour le statut formation du stagiaire
  const updateStatutFormation = async (newStatut) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}`, {
        user_id: stagiaireId,
        statut_formation: newStatut
      });
      setStagiaire(prev => ({ ...prev, statut_formation: newStatut }));
      toast.success('Statut mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du statut');
    }
  };

  // Mettre à jour le type de contrat du stagiaire
  const updateTypeContrat = async (newType) => {
    try {
      await axios.put(`/stagiaires/${stagiaireId}`, {
        user_id: stagiaireId,
        type_contrat: newType
      });
      setStagiaire(prev => ({ ...prev, type_contrat: newType }));
      toast.success('Type de contrat mis à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour du type de contrat');
    }
  };

  // Supprimer un stagiaire
  const deleteStagiaire = async () => {
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer ${stagiaire.user_info?.prenom} ${stagiaire.user_info?.nom} ?\n\nCette action est irréversible et supprimera toutes les données associées (documents, notes, présences, etc.)`)) {
      return;
    }
    
    try {
      await axios.delete(`/stagiaires/${stagiaireId}`);
      toast.success('Stagiaire supprimé');
      navigate(backUrl);
    } catch (error) {
      toast.error('Erreur lors de la suppression du stagiaire');
    }
  };

  // Télécharger l'archive avant bascule
  const downloadArchive = async () => {
    try {
      const response = await axios.get(`/moniteur/generate-archive/${stagiaireId}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Dossier_fin_formation_${stagiaire.user_info?.nom}_${stagiaire.user_info?.prenom}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setArchiveDownloaded(true);
      toast.success('Archive téléchargée');
    } catch (error) {
      toast.error('Erreur lors du téléchargement de l\'archive');
    }
  };

  // Convertir en Moniteur VNM
  const convertToMoniteur = async () => {
    const stagNom = `${stagiaire.user_info?.prenom} ${stagiaire.user_info?.nom}`;
    if (!window.confirm(
      `Avez-vous bien envoyé les questionnaires qualité ?\n` +
      `• Questionnaire diplôme → au stagiaire\n` +
      `• Questionnaire formateur → au formateur\n` +
      `• Questionnaire tuteur → au tuteur\n` +
      `• Questionnaire entreprise → à l'entreprise\n\n` +
      `Voulez-vous confirmer la conversion de ${stagNom} en Moniteur VNM ?\n` +
      `Le stagiaire sera archivé et un profil Moniteur VNM sera créé.`
    )) {
      return;
    }
    
    setConverting(true);
    try {
      await axios.post(`/moniteur/convert-stagiaire/${stagiaireId}`);
      toast.success('Stagiaire converti en Moniteur VNM !');
      // Rediriger vers la liste des moniteurs
      navigate('/moniteurs');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la conversion');
    } finally {
      setConverting(false);
    }
  };

  return (
    <>
    <div className="space-y-6" data-testid="fiche-stagiaire-centre">
      {/* Header avec bandeau vert */}
      <div className="bg-gradient-to-r from-[#8DC63F] to-[#6ba32e] text-white rounded-lg p-4 sm:p-6 shadow-md overflow-hidden">
        {/* Top row: back button + actions */}
        <div className="flex items-center justify-between gap-2 mb-4">
          <Link to={backUrl}>
            <Button variant="outline" size="sm" className="bg-white/20 border-white/30 text-white hover:bg-white/30">
              <ArrowLeft className="h-4 w-4 mr-1" />
              Retour
            </Button>
          </Link>
          <div className="flex flex-wrap gap-2 justify-end">
            {!readOnly && bcValidated && (user?.role === 'centre' || user?.role === 'responsable_pedagogique') && (
              <>
                <Link to="/satisfaction">
                  <Button 
                    className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                    variant="outline"
                    size="sm"
                    data-testid="send-questionnaires-btn"
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Questionnaires qualité
                  </Button>
                </Link>
                <Button 
                  onClick={checkGraduateEligibility}
                  disabled={converting}
                  className="bg-amber-500 hover:bg-amber-600 text-white animate-pulse shadow-lg shadow-amber-500/50 ring-2 ring-amber-300 ring-offset-1"
                  size="sm"
                  data-testid="convert-moniteur-btn"
                >
                  {converting ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <GraduationCap className="h-4 w-4 mr-2" />
                  )}
                  Moniteur VNM
                </Button>
              </>
            )}
            <Button onClick={downloadAllDocumentsZip} disabled={zipLoading} className="bg-white/20 hover:bg-white/30 text-white border-white/30" variant="outline" size="sm" data-testid="download-zip">
              {zipLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FolderArchive className="h-4 w-4 mr-2" />}
              {zipLoading ? 'Préparation...' : 'Dossier complet (ZIP)'}
            </Button>
            <Button onClick={downloadCarnetNotes} className="bg-white text-[#8DC63F] hover:bg-white/90" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Carnet Notes PDF
            </Button>
            <Button onClick={downloadFeuillePresence} className="bg-white/20 hover:bg-white/30 text-white border-white/30" variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Presence PDF
            </Button>
            <Button onClick={() => setCarteOpen(true)} className="bg-white text-blue-600 hover:bg-blue-50" size="sm" data-testid="btn-carte-etudiant">
              <CreditCard className="h-4 w-4 mr-2" />
              Carte Etudiant
            </Button>
            {!readOnly && (user?.role === 'centre' || user?.role === 'responsable_pedagogique') && (
              <Button 
                onClick={deleteStagiaire} 
                className="bg-red-500 hover:bg-red-600 text-white"
                size="sm"
                data-testid="delete-stagiaire-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Supprimer
              </Button>
            )}
          </div>
        </div>

        {/* Info row: photo + details */}
        <div className="flex items-start gap-4">
          {/* Photo du stagiaire depuis la source unique serveur */}
          <PhotoIdentite
            userId={stagiaire.user_id}
            alt={`${stagiaire.user_info?.prenom} ${stagiaire.user_info?.nom}`}
            className="w-16 h-20 rounded-lg object-cover border-4 border-white/50 shadow-lg flex-shrink-0"
            testId="fiche-stagiaire-photo"
            fallback={
              <div className="w-16 h-20 bg-white/20 rounded-lg flex items-center justify-center border-4 border-white/50 flex-shrink-0">
                <span className="text-2xl font-bold">
                  {stagiaire.user_info?.prenom?.[0]}{stagiaire.user_info?.nom?.[0]}
                </span>
              </div>
            }
          />
          
          <div className="min-w-0 flex-1">
            {readOnly && (
              <span className="inline-block px-2 py-0.5 text-xs bg-white/20 rounded mb-1">Consultation seule</span>
            )}
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-display font-bold truncate">
              {stagiaire.genre === 'M.' ? 'M.' : stagiaire.genre === 'Mme' ? 'Mme' : stagiaire.genre || ''} {stagiaire.user_info?.prenom} {stagiaire.user_info?.nom}
              <span className="ml-2 text-base font-mono bg-white/30 px-2 py-0.5 rounded" data-testid="stagiaire-badge-numero-candidat">
                N°{numeroCandidatCRM || stagiaireId?.substring(0, 8).toUpperCase()}
              </span>
            </h1>
            <p className="text-white/80 text-sm">
              {stagiaire.date_naissance ? `Né(e) le ${new Date(stagiaire.date_naissance).toLocaleDateString('fr-FR')}` : ''} 
              {stagiaire.date_naissance && stagiaire.centre_formation && ' - '}
              {stagiaire.centre_formation}
            </p>
            <p className="text-xs text-white/70 mt-1">
              BPJEPS Activités du Motonautisme et Disciplines Associées
            </p>
            
            {/* Sélecteur de statut formation */}
            {!readOnly && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-white/80">Statut :</label>
                  <select
                    value={stagiaire.statut_formation || 'en_formation'}
                    onChange={(e) => updateStatutFormation(e.target.value)}
                    className="px-3 py-1 rounded-lg text-sm bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                    data-testid="select-statut-formation"
                  >
                    <option value="en_formation" className="text-gray-800">En formation</option>
                    <option value="diplome" className="text-gray-800">Diplômé(e)</option>
                    <option value="redoublant" className="text-gray-800">Redoublant</option>
                    <option value="abandon" className="text-gray-800">Abandon</option>
                  </select>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-white/80">Type :</label>
                  <select
                    value={stagiaire.type_contrat || ''}
                    onChange={(e) => updateTypeContrat(e.target.value)}
                    className="px-3 py-1 rounded-lg text-sm bg-white/20 border border-white/30 text-white focus:outline-none focus:ring-2 focus:ring-white/50"
                    data-testid="select-type-contrat"
                  >
                    <option value="" className="text-gray-800">-- Sélectionner --</option>
                    <option value="stagiaire_fp" className="text-gray-800">Stagiaire de la formation professionnelle</option>
                    <option value="apprenti" className="text-gray-800">Apprenti</option>
                    <option value="contrat_pro" className="text-gray-800">Contrat de professionnalisation</option>
                    <option value="salarie" className="text-gray-800">Salarié</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Bandeau 3 BC validés */}
        {bcValidated && (
          <div className="mt-4 p-3 bg-emerald-500/20 border border-emerald-300/50 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle className="h-5 w-5 text-emerald-200" />
              <span className="font-medium">Les 3 Blocs de Compétences sont validés !</span>
              <span className="text-sm text-white/80">- Ce stagiaire est éligible au diplôme</span>
            </div>
          </div>
        )}
        
        {/* Infos entreprise et tuteur à côté du statut */}
        {(tuteurInfo || stagiaire.entreprise_id) && (
          <div className="mt-4 pt-4 border-t border-white/20 flex flex-wrap gap-4 text-sm">
            {(stagiaire.entreprise_nom || entrepriseInfo?.nom_entreprise || entrepriseInfo?.nom) && (
              <span className="flex items-center gap-1">
                <Building2 className="h-4 w-4" />
                Entreprise: <strong>{stagiaire.entreprise_nom || entrepriseInfo?.nom_entreprise || entrepriseInfo?.nom}</strong>
              </span>
            )}
            {tuteurInfo && (
              <span className="flex items-center gap-1">
                <UserCheck className="h-4 w-4" />
                Tuteur: <strong>{tuteurInfo.prenom} {tuteurInfo.nom}</strong>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Alertes */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Documents manquants */}
        <div className={`p-4 rounded-lg border ${documentsManquants.length > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-3">
            {documentsManquants.length > 0 ? (
              <AlertTriangle className="h-8 w-8 text-red-500" />
            ) : (
              <CheckCircle className="h-8 w-8 text-green-500" />
            )}
            <div>
              <p className="font-semibold text-[#0F172A]">Documents</p>
              {documentsManquants.length > 0 ? (
                <p className="text-sm text-red-600">{documentsManquants.length} document(s) manquant(s)</p>
              ) : (
                <p className="text-sm text-green-600">Tous les documents sont fournis</p>
              )}
            </div>
          </div>
          {documentsManquants.length > 0 && (
            <div className="mt-2 text-xs text-red-600">
              Manquants: {documentsManquants.map(d => d.label).join(', ')}
            </div>
          )}
        </div>

        {/* Absences */}
        <div className={`p-4 rounded-lg border ${totalAbsences > 0 ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-3">
            {totalAbsences > 0 ? (
              <XCircle className="h-8 w-8 text-red-500" />
            ) : (
              <CheckCircle className="h-8 w-8 text-green-500" />
            )}
            <div>
              <p className="font-semibold text-[#0F172A]">Absences</p>
              <p className={`text-sm ${totalAbsences > 0 ? 'text-red-600' : 'text-green-600'}`}>
                {totalAbsences} absence(s)
              </p>
              {totalAbsences > 0 && (
                <div className="mt-1 text-xs space-y-0.5">
                  <p className="text-blue-600">Centre: {absencesCentre}</p>
                  <p className="text-orange-600">Entreprise: {absencesEntreprise}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Retards */}
        <div className={`p-4 rounded-lg border ${totalRetards > 0 ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
          <div className="flex items-center gap-3">
            {totalRetards > 0 ? (
              <Clock className="h-8 w-8 text-orange-500" />
            ) : (
              <CheckCircle className="h-8 w-8 text-green-500" />
            )}
            <div>
              <p className="font-semibold text-[#0F172A]">Retards</p>
              <p className={`text-sm ${totalRetards > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                {totalRetards} retard(s)
              </p>
              {totalRetards > 0 && (
                <div className="mt-1 text-xs space-y-0.5">
                  <p className="text-blue-600">Centre: {retardsCentre}</p>
                  <p className="text-orange-600">Entreprise: {retardsEntreprise}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Facturation et Paiement */}
        <div 
          className={`p-4 rounded-lg border cursor-pointer transition-all hover:shadow-md relative ${
            prospectLink ? 'bg-blue-50 border-blue-200 hover:bg-blue-100' : 'bg-slate-50 border-slate-200 hover:bg-slate-100'
          }`}
          onClick={() => {
            if (prospectLink?.prospect_id) {
              navigate(`/crm?prospect=${prospectLink.prospect_id}`);
            } else {
              openLinkModal();
            }
          }}
          data-testid="facturation-paiement-card"
        >
          <div className="flex items-center gap-3">
            <Euro className="h-8 w-8 text-blue-500" />
            <div>
              <p className="font-semibold text-[#0F172A]">Facturation</p>
              {prospectLink ? (
                <>
                  <p className="text-sm text-blue-600">
                    {prospectLink.prospect?.devis_count || 0} devis · {prospectLink.prospect?.facture_count || 0} facture(s)
                  </p>
                  <p className="text-xs text-blue-500 mt-0.5">Cliquer pour ouvrir la fiche</p>
                </>
              ) : (
                <p className="text-sm text-slate-500">Non lié — Cliquer pour lier</p>
              )}
            </div>
          </div>
          {prospectLink && (
            <button
              onClick={(e) => { e.stopPropagation(); openLinkModal(); }}
              className="absolute top-2 right-2 text-xs text-slate-400 hover:text-blue-600"
              title="Changer la liaison"
            >
              Modifier
            </button>
          )}
        </div>
      </div>

      {/* Info Stagiaire */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <User className="h-5 w-5 text-[#8DC63F]" />
          Informations
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-slate-500">Email</p>
            <p className="font-medium">{stagiaire.user_info?.email}</p>
          </div>
          <div>
            <p className="text-slate-500">Téléphone</p>
            <p className="font-medium">{stagiaire.user_info?.telephone || stagiaire.telephone || '-'}</p>
          </div>
          <div>
            <p className="text-slate-500">Date de naissance</p>
            <p className="font-medium">{stagiaire.date_naissance || '-'}</p>
          </div>
          <div>
            <p className="text-slate-500">Lieu de naissance</p>
            <p className="font-medium">{stagiaire.lieu_de_naissance || '-'}</p>
          </div>
          <div>
            <p className="text-slate-500">Adresse</p>
            <p className="font-medium">{stagiaire.adresse || '-'}</p>
          </div>
          <div>
            <p className="text-slate-500">Code postal / Ville</p>
            <p className="font-medium">{stagiaire.code_postal || '-'} {stagiaire.ville || ''}</p>
          </div>
          <div>
            <p className="text-slate-500">Groupe sanguin</p>
            <p className="font-medium">{stagiaire.groupe_sanguin || '-'}</p>
          </div>
          <div>
            <p className="text-slate-500">Nationalité</p>
            <p className="font-medium">{stagiaire.nationalite || '-'}</p>
          </div>
          {stagiaire.allergies && (
            <div className="col-span-2">
              <p className="text-slate-500">Allergies</p>
              <p className="font-medium text-red-600">{stagiaire.allergies}</p>
            </div>
          )}
          {stagiaire.traitements && (
            <div className="col-span-2">
              <p className="text-slate-500">Traitements</p>
              <p className="font-medium">{stagiaire.traitements}</p>
            </div>
          )}
          <div>
            <p className="text-slate-500">Tuteur</p>
            <p className="font-medium">
              {stagiaire.tuteur_info ? `${stagiaire.tuteur_info.prenom} ${stagiaire.tuteur_info.nom}` : 'Non attribué'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Entreprise</p>
            <p className="font-medium">
              {stagiaire.entreprise_nom || entrepriseInfo?.nom_entreprise || stagiaire.entreprise_info?.nom || 'Non attribuée'}
            </p>
          </div>
          <div>
            <p className="text-slate-500">Moyenne générale</p>
            <p className={`text-2xl font-bold ${moyenneGenerale >= 7 ? 'text-green-600' : moyenneGenerale >= 4 ? 'text-orange-500' : 'text-red-500'}`}>
              {moyenneGenerale}/10
            </p>
          </div>
        </div>
      </div>

      {/* Compétences Validées */}
      {autonomiesValidees.length > 0 && (
        <CompetencesValidees autonomies={autonomiesValidees} />
      )}

      {/* Contact d'urgence */}
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-red-800 mb-4 flex items-center gap-2">
          <Phone className="h-5 w-5 text-red-600" />
          Contact d'Urgence
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <div>
            <p className="text-red-600">Nom du contact</p>
            <p className="font-medium text-[#0F172A]">
              {stagiaire.contact_urgence_prenom || ''} {stagiaire.contact_urgence_nom || 'Non renseigné'}
            </p>
          </div>
          <div>
            <p className="text-red-600">Téléphone</p>
            <p className="font-bold text-lg text-[#0F172A]">{stagiaire.contact_urgence_telephone || 'Non renseigné'}</p>
          </div>
          <div>
            <p className="text-red-600">Lien de parenté</p>
            <p className="font-medium text-[#0F172A]">{stagiaire.contact_urgence_filiation || 'Non renseigné'}</p>
          </div>
        </div>
      </div>

      {/* Informations Entreprise */}
      {entrepriseInfo && (
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-orange-800 mb-4 flex items-center gap-2">
            <Building2 className="h-5 w-5 text-orange-600" />
            Entreprise - {entrepriseInfo.nom_entreprise || entrepriseInfo.nom || 'N/A'}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3">Coordonnées</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-slate-500">Adresse:</span> {entrepriseInfo.adresse || '-'}</p>
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  {entrepriseInfo.telephone || entrepriseInfo.user_info?.telephone || '-'}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {entrepriseInfo.email || entrepriseInfo.user_info?.email || '-'}
                </p>
                {entrepriseInfo.siret && (
                  <p><span className="text-slate-500">SIRET:</span> {entrepriseInfo.siret}</p>
                )}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3">Documents entreprise ({entrepriseDocs.length})</h3>
              {entrepriseDocs.length === 0 ? (
                <p className="text-slate-500 text-sm">Aucun document uploadé</p>
              ) : (
                <div className="space-y-2">
                  {entrepriseDocs.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-orange-100">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-orange-500" />
                        <span>{doc.document_type || doc.type || 'Document'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => viewDocument(doc.document_id)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="Voir"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_entreprise`)}
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors"
                          title="Télécharger"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => deleteDocument(doc.document_id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Informations Formateur */}
      {formateurInfo && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 shadow-sm" data-testid="formateur-docs-section">
          <h2 className="text-xl font-display font-semibold text-emerald-800 mb-4 flex items-center gap-2">
            <GraduationCap className="h-5 w-5 text-emerald-600" />
            Formateur - {formateurInfo.prenom || ''} {formateurInfo.nom || ''}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3">Coordonnées</h3>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  {formateurInfo.telephone || '-'}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {formateurInfo.email || '-'}
                </p>
              </div>
            </div>
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3">Documents formateur ({formateurDocs.length})</h3>
              {formateurDocs.length === 0 ? (
                <p className="text-slate-500 text-sm">Aucun document uploadé</p>
              ) : (
                <div className="space-y-2">
                  {formateurDocs.map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-emerald-100">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-emerald-500" />
                        <span>{doc.document_type || doc.type || 'Document'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button onClick={() => viewDocument(doc.document_id)} className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors" title="Voir">
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_formateur`)} className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors" title="Télécharger">
                          <Download className="h-3.5 w-3.5" />
                        </button>
                        <button onClick={() => deleteDocument(doc.document_id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors" title="Supprimer">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Informations Tuteur */}
      {tuteurInfo && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 shadow-sm">
          <h2 className="text-xl font-display font-semibold text-purple-800 mb-4 flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-purple-600" />
            Tuteur - {tuteurInfo.user_info?.prenom || tuteurInfo.prenom || ''} {tuteurInfo.user_info?.nom || tuteurInfo.nom || ''}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3">Coordonnées</h3>
              <div className="space-y-2 text-sm">
                <p className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-slate-400" />
                  {tuteurInfo.telephone || tuteurInfo.user_info?.telephone || '-'}
                </p>
                <p className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-slate-400" />
                  {tuteurInfo.user_info?.email || '-'}
                </p>
                {tuteurInfo.entreprise && (
                  <p><span className="text-slate-500">Entreprise:</span> {tuteurInfo.entreprise}</p>
                )}
              </div>
            </div>
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3 flex items-center gap-2">
                <Award className="h-4 w-4 text-purple-500" />
                Diplômes ({tuteurDocs.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome')).length})
              </h3>
              {tuteurDocs.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome')).length === 0 ? (
                <p className="text-slate-500 text-sm">Aucun diplôme uploadé</p>
              ) : (
                <div className="space-y-2">
                  {tuteurDocs.filter(d => (d.document_type || d.type || '').toLowerCase().includes('diplome')).map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-purple-100">
                      <div className="flex items-center gap-2">
                        <Award className="h-4 w-4 text-purple-500" />
                        <span className="truncate max-w-[120px]">{doc.document_type || doc.type || 'Diplôme'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => viewDocument(doc.document_id)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="Voir"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'diplome'}_tuteur`)}
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors"
                          title="Télécharger"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div>
              <h3 className="font-medium text-[#0F172A] mb-3">Autres documents ({tuteurDocs.filter(d => !(d.document_type || d.type || '').toLowerCase().includes('diplome')).length})</h3>
              {tuteurDocs.filter(d => !(d.document_type || d.type || '').toLowerCase().includes('diplome')).length === 0 ? (
                <p className="text-slate-500 text-sm">Aucun autre document</p>
              ) : (
                <div className="space-y-2">
                  {tuteurDocs.filter(d => !(d.document_type || d.type || '').toLowerCase().includes('diplome')).map((doc, idx) => (
                    <div key={idx} className="flex items-center justify-between text-sm bg-white p-2 rounded border border-purple-100">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-purple-500" />
                        <span className="truncate max-w-[120px]">{doc.document_type || doc.type || 'Document'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => viewDocument(doc.document_id)}
                          className="p-1.5 text-blue-500 hover:bg-blue-50 rounded transition-colors"
                          title="Voir"
                        >
                          <Eye className="h-3.5 w-3.5" />
                        </button>
                        <button
                          onClick={() => downloadDocument(doc.document_id, `${doc.document_type || 'doc'}_tuteur`)}
                          className="p-1.5 text-green-500 hover:bg-green-50 rounded transition-colors"
                          title="Télécharger"
                        >
                          <Download className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Devis de formation */}
      {(devisData || prospectLink) && (
        <div className="bg-white border-2 border-teal-200 rounded-lg shadow-sm" data-testid="stagiaire-devis-section">
          <div className="bg-teal-100/50 px-6 py-4 border-b border-teal-200">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-display font-semibold text-[#0F172A] flex items-center gap-2">
                <FileText className="h-5 w-5 text-teal-600" />
                Devis de formation
              </h2>
              {devisData?.statut === 'en_attente_signature' ? (
                <span className="text-xs font-medium bg-amber-500 text-white px-2 py-1 rounded-full">En attente de signature</span>
              ) : devisData?.statut === 'signe' ? (
                <span className="text-xs font-medium bg-green-500 text-white px-2 py-1 rounded-full">Signé</span>
              ) : devisData ? (
                <span className="text-xs font-medium bg-teal-500 text-white px-2 py-1 rounded-full">Brouillon</span>
              ) : (
                <span className="text-xs font-medium bg-slate-200 text-slate-600 px-2 py-1 rounded-full">Non généré</span>
              )}
            </div>
          </div>
          <div className="p-6 space-y-3">
            {devisData ? (
              <>
                <div className="bg-teal-50 rounded-lg p-3 border border-teal-100 text-sm space-y-1" data-testid="stagiaire-devis-info">
                  <p><span className="text-slate-500">N° Devis :</span> <span className="font-medium">{devisData.devis_numero || devisData.devis_id}</span></p>
                  <p><span className="text-slate-500">Date :</span> <span className="font-medium">{devisData.date_creation ? new Date(devisData.date_creation).toLocaleDateString('fr-FR') : '-'}</span></p>
                  <p><span className="text-slate-500">Montant :</span> <span className="font-semibold text-teal-700">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(devisData.montant_total || 0)}</span></p>
                  {devisData.bc_heures && Object.keys(devisData.bc_heures).length > 0 && (
                    <div className="pt-1 border-t border-teal-50 mt-1">
                      {Object.entries(devisData.bc_heures).map(([bc, h]) => (
                        <p key={bc}><span className="text-slate-500">{bc} :</span> <span className="font-medium">{h}h</span></p>
                      ))}
                      {devisData.nb_heures_foad > 0 && (
                        <p><span className="text-slate-500">FOAD :</span> <span className="font-medium">{devisData.nb_heures_foad}h</span></p>
                      )}
                      <p className="font-medium text-teal-600 pt-1">Total : {(devisData.nb_heures || 0) + (devisData.nb_heures_foad || 0)}h</p>
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={handleViewDevis} className="border-teal-300 text-teal-700 hover:bg-teal-100" data-testid="stagiaire-btn-view-devis" title="Prévisualiser">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={handleDownloadDevis} className="border-teal-300 text-teal-700 hover:bg-teal-100" data-testid="stagiaire-btn-download-devis">
                    <Download className="h-4 w-4 mr-1" />
                    Télécharger
                  </Button>
                  {!readOnly && devisData.statut !== 'en_attente_signature' && devisData.statut !== 'signe' && (
                    <Button variant="outline" size="sm" onClick={handleSendDevis} disabled={sendingDevis} className="border-amber-300 text-amber-700 hover:bg-amber-100" data-testid="stagiaire-btn-send-devis">
                      {sendingDevis ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                      Envoyer pour signature
                    </Button>
                  )}
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500 text-center py-2">Aucun devis disponible. Générez-en un depuis la fiche candidat ou le CRM.</p>
            )}
          </div>
        </div>
      )}

      {/* Convention de formation */}
      <ConventionFormationSection
        stagiaireId={stagiaireId}
        stagiaire={stagiaire}
        conventionData={dossierCandidat?.convention_data}
      />

      {/* Règlement intérieur (envoi manuel à la rentrée, signature du stagiaire) */}
      <ReglementInterieurSection stagiaireId={stagiaireId} stagiaire={stagiaire} peutEnvoyer={!readOnly && ['centre', 'direction'].includes(user?.role)} />

      {/* Convention de Mise en Situation Pédagogique */}
      <ConventionStageSection
        stagiaireId={stagiaireId}
        stagiaire={stagiaire}
        convStage={convStage}
        setConvStage={setConvStage}
        readOnly={readOnly}
      />

      {/* Autres conventions (lecture seule) */}
      {autresConventions.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="stagiaire-autres-conventions-section">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2 mb-1">
            <FileText className="h-5 w-5 text-orange-500" />
            Autres conventions
          </h2>
          <p className="text-xs text-orange-600 mb-4">apprentissage, transition pro., région...</p>
          <div className="space-y-3">
            {autresConventions.map((doc) => (
              <div key={doc.doc_id} className="flex items-center justify-between p-3 bg-orange-50 rounded-lg border border-orange-100" data-testid={`stagiaire-convention-row-${doc.doc_id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileSignature className="h-4 w-4 text-orange-500 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-700">{doc.label}</p>
                    <p className="text-xs text-slate-500 truncate">{doc.filename}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleViewAutreConvention(doc.doc_id)} className="text-orange-700 hover:bg-orange-100" title="Prévisualiser">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDownloadAutreConvention(doc.doc_id, doc.filename)} className="text-orange-700 hover:bg-orange-100" title="Télécharger">
                    <Download className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Documents */}
      <StagiaireDocumentsSection
        documents={documents}
        candidatDocuments={candidatDocuments}
        stagiaire={stagiaire}
        readOnly={readOnly}
        onRefresh={async () => {
          const docsRes = await axios.get(`/documents/${stagiaireId}`).catch(() => ({ data: [] }));
          setDocuments(docsRes.data);
          const candidatDocsRes = await axios.get(`/candidats/${stagiaireId}/documents`).catch(() => ({ data: { documents: [] } }));
          setCandidatDocuments(candidatDocsRes.data?.documents || candidatDocsRes.data || []);
        }}
      />

      {/* Graphique de progression avec filtres */}
      {notes.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
            <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-[#8DC63F]" />
              Progression des Notes
            </h2>
            {/* Filtres */}
            <div className="flex flex-wrap gap-2">
              <select
                value={filterCategorie}
                onChange={(e) => {
                  setFilterCategorie(e.target.value);
                  setFilterArticle(''); // Reset article quand catégorie change
                }}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              >
                <option value="">Toutes catégories</option>
                {getUniqueCategories().map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                value={filterArticle}
                onChange={(e) => setFilterArticle(e.target.value)}
                className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
                disabled={!filterCategorie && getUniqueArticles().length === 0}
              >
                <option value="">Tous articles</option>
                {getUniqueArticles().map(art => (
                  <option key={art} value={art}>{art}</option>
                ))}
              </select>
              {(filterCategorie || filterArticle) && (
                <button
                  onClick={() => { setFilterCategorie(''); setFilterArticle(''); }}
                  className="px-3 py-1.5 text-sm bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200"
                >
                  Réinitialiser
                </button>
              )}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={prepareNotesChartData()}>
                <ReferenceArea y1={0} y2={4} fill="#EF4444" fillOpacity={0.1} />
                <ReferenceArea y1={4} y2={7} fill="#F59E0B" fillOpacity={0.1} />
                <ReferenceArea y1={7} y2={10} fill="#22C55E" fillOpacity={0.1} />
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 12 }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  const details = data.details || [];
                  return (
                    <div className="bg-white border border-slate-200 rounded-lg p-3 shadow-lg max-w-xs">
                      <p className="font-semibold text-sm text-slate-800 mb-1">{data.date} &mdash; <span className="text-[#8DC63F]">{data.note}/10</span></p>
                      {details.map((d, i) => (
                        <div key={i} className="mb-1 last:mb-0">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs"><span className="font-medium text-slate-700">{d.categorie}</span>{d.item && <span className="text-slate-400"> / {d.item}</span>}</span>
                            <span className="text-xs font-bold">{d.note.toFixed(0)}/10</span>
                          </div>
                          {d.commentaire && <p className="text-[10px] text-slate-400 truncate">{d.commentaire.substring(0, 70)}{d.commentaire.length > 70 ? '...' : ''}</p>}
                        </div>
                      ))}
                    </div>
                  );
                }} />
                <Line 
                  type="monotone" 
                  dataKey="note" 
                  stroke="#8DC63F" 
                  strokeWidth={3}
                  dot={{ fill: '#8DC63F', strokeWidth: 2, r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {(filterCategorie || filterArticle) && (
            <p className="text-xs text-slate-500 mt-2">
              Filtre actif: {filterCategorie || 'Toutes'} {filterArticle ? `> ${filterArticle}` : ''}
            </p>
          )}
        </div>
      )}

      {/* Carnets de Notes - 2 Onglets */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveCarnet('formateur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-colors ${
              activeCarnet === 'formateur'
                ? 'bg-[#8DC63F]/10 border-b-2 border-[#8DC63F] text-[#8DC63F]'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
          >
            <BookOpen className="h-5 w-5" />
            <span className="font-medium">Carnet Formateur</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeCarnet === 'formateur' ? 'bg-[#8DC63F]/20' : 'bg-slate-200'
            }`}>{notes.length}</span>
          </button>
          <button
            onClick={() => setActiveCarnet('tuteur')}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 transition-colors ${
              activeCarnet === 'tuteur'
                ? 'bg-orange-50 border-b-2 border-orange-500 text-orange-600'
                : 'hover:bg-slate-50 text-slate-600'
            }`}
          >
            <Award className="h-5 w-5" />
            <span className="font-medium">Carnet Tuteur</span>
            <span className={`px-2 py-0.5 text-xs rounded-full ${
              activeCarnet === 'tuteur' ? 'bg-orange-100' : 'bg-slate-200'
            }`}>{competences.length}</span>
          </button>
        </div>
        <div className="p-6">
          {activeCarnet === 'formateur' ? (
            <CarnetNotesFormateur 
              notes={notes} 
              title="Notes du Centre de Formation"
            />
          ) : (
            <CarnetNotesTuteur 
              evaluations={competences} 
              heuresParCategorie={heuresParCategorie}
              validationsAutonomie={validationsAutonomie}
              autonomiesValidees={autonomiesValidees}
              autonomieHistorique={autonomieHistorique}
              title="Compétences évaluées par le Tuteur"
            />
          )}
        </div>
      </div>

      {/* Rapport d'Analyse IA */}
      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden" data-testid="analyse-ia-section">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-violet-500 to-purple-600 rounded-lg">
                <Brain className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-display font-semibold text-[#0F172A]">Rapport d'Analyse IA</h2>
                <p className="text-xs text-slate-500">Analyse automatique des compétences et de la progression</p>
              </div>
            </div>
            {analyseIA?.created_at && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {new Date(analyseIA.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                {analyseIA.cached && <span className="ml-1 px-1.5 py-0.5 bg-blue-100 text-blue-600 rounded text-xs">cache</span>}
              </span>
            )}
          </div>
          {analyseIALoading ? (
            <div className="flex items-center gap-2 text-slate-500 py-6 justify-center">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span className="text-sm">Chargement de l'analyse...</span>
            </div>
          ) : analyseIA?.analyse ? (
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-100 max-h-[400px] overflow-y-auto">
              {analyseIA.analyse.split('\n').map((line, i) => {
                if (line.startsWith('**') && line.includes('**')) {
                  const title = line.replace(/\*\*/g, '');
                  return <h3 key={i} className="font-bold text-[#0F172A] mt-4 mb-1 text-sm border-b border-slate-200 pb-1">{title}</h3>;
                }
                if (line.startsWith('- ') || line.startsWith('• ')) {
                  return <li key={i} className="text-sm text-slate-700 ml-4 mb-1">{line.slice(2)}</li>;
                }
                if (line.trim() === '') return <br key={i} />;
                return <p key={i} className="text-sm text-slate-700 mb-1">{line}</p>;
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-slate-400">
              <Brain className="h-10 w-10 mx-auto mb-2 opacity-30" />
              <p className="text-sm">Aucune analyse IA disponible pour ce stagiaire.</p>
              <p className="text-xs mt-1">L'analyse peut être générée depuis l'espace Progression du stagiaire.</p>
            </div>
          )}
        </div>
      </div>


      {/* Épreuves EPMSP */}
      <EPMSPConsultation 
        stagiaireId={stagiaireId} 
        stagiaireNom={`${stagiaire.user_info?.prenom} ${stagiaire.user_info?.nom}`}
      />

      {/* Prérequis et Blocs de Compétences */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
          <Award className="h-5 w-5 text-[#8DC63F]" />
          Prérequis et Blocs de Compétences
        </h2>
        <CompetencesPrerequisConsultation stagiaireId={stagiaireId} />
      </div>

      {/* Grille d'Évaluation Tuteur */}
      <GrilleEvaluationCentreSection
        stagiaireId={stagiaireId}
        grilleEval={grilleEval}
        grilleStructure={grilleStructure}
      />

      {/* Bilans Mensuels Entreprise */}
      <BilansMensuelsSection bilansEntreprise={bilansEntreprise} />

      {/* Paiements candidat & PIF signé (section Historique) */}
      {(() => {
        const hasPayedTests = candidatPaiements.some(p => (p.type === 'tests_selection' || p.description?.includes('test')) && (p.statut === 'completed' || p.payment_status === 'paid'));
        const hasPayedPrepa = candidatPaiements.some(p => (p.type === 'journee_preparation' || p.description?.includes('journée') || p.description?.includes('préparation')) && (p.statut === 'completed' || p.payment_status === 'paid'));
        const pifSignedByCandidat = dossierCandidat?.pif_data?.statut === 'signe';
        return (
          <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="paiements-historique-section">
            <h2 className="text-xl font-display font-semibold text-[#0F172A] mb-4 flex items-center gap-2">
              <Euro className="h-5 w-5 text-[#8DC63F]" />
              Paiements
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className={`p-4 rounded-lg border ${hasPayedTests ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`} data-testid="paiement-tests-card">
                <div className="flex items-center gap-3">
                  <Euro className={`h-8 w-8 ${hasPayedTests ? 'text-green-500' : 'text-slate-400'}`} />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Tests de sélection</p>
                    <p className={`text-sm ${hasPayedTests ? 'text-green-600' : 'text-slate-500'}`}>
                      {hasPayedTests ? 'Payé' : 'Non payé'}
                    </p>
                  </div>
                </div>
              </div>
              <div className={`p-4 rounded-lg border ${hasPayedPrepa ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-200'}`} data-testid="paiement-journee-card">
                <div className="flex items-center gap-3">
                  <Calendar className={`h-8 w-8 ${hasPayedPrepa ? 'text-green-500' : 'text-slate-400'}`} />
                  <div>
                    <p className="font-semibold text-[#0F172A]">Journée de préparation</p>
                    <p className={`text-sm ${hasPayedPrepa ? 'text-green-600' : 'text-slate-500'}`}>
                      {hasPayedPrepa ? 'Inscrit & Payé' : 'Non inscrit'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Dossier Candidat (Historique) */}
      <DossierCandidatSection
        stagiaireId={stagiaireId}
        stagiaire={stagiaire}
        dossierCandidat={dossierCandidat}
      />

      {/* Historique de Présence - Menu déroulant */}
      <PresenceHistoryCollapsible presences={presences} />

      {/* Historique des Modifications (Traçabilité Qualiopi) */}
      {user?.role === 'centre' && (
        <HistoriqueModificationsSection stagiaireId={stagiaireId} />
      )}

      {/* Modal Diplômer - Conversion en Moniteur VNM */}
      <Dialog open={showGraduateModal} onOpenChange={(open) => {
        if (!open) {
          setArchiveDownloaded(false);
        }
        setShowGraduateModal(open);
      }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <GraduationCap className="h-6 w-6 text-amber-500" />
              Diplômer le stagiaire
            </DialogTitle>
            <DialogDescription>
              Convertir {stagiaire?.user_info?.prenom} {stagiaire?.user_info?.nom} en Moniteur VNM diplômé
            </DialogDescription>
          </DialogHeader>

          {eligibilityData && (
            <div className="space-y-4">
              {/* Statut des BC */}
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium mb-3">Validation des Blocs de Compétences</h4>
                <div className="grid grid-cols-3 gap-3">
                  {['BC1', 'BC2', 'BC3'].map((bc) => {
                    const isValid = eligibilityData[`${bc.toLowerCase()}_valide`];
                    return (
                      <div 
                        key={bc}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border ${
                          isValid 
                            ? 'bg-green-50 border-green-200 text-green-700' 
                            : 'bg-red-50 border-red-200 text-red-700'
                        }`}
                      >
                        {isValid ? <CheckCircle className="h-5 w-5" /> : <XCircle className="h-5 w-5" />}
                        <span className="font-medium">{bc}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {eligibilityData.eligible ? (
                <>
                  {/* Instructions */}
                  <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div className="text-sm text-amber-800">
                        <p className="font-medium mb-2">Action irréversible</p>
                        <ul className="list-disc list-inside space-y-1 text-amber-700">
                          <li>Le profil stagiaire sera <strong>archivé</strong></li>
                          <li>Un nouveau profil <strong>Moniteur VNM</strong> sera créé</li>
                          <li>Les identifiants de connexion restent inchangés</li>
                          <li>Vous devez <strong>télécharger l'archive</strong> avant de confirmer</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Étape 1 : Télécharger l'archive */}
                  <div className={`p-4 rounded-lg border-2 ${archiveDownloaded ? 'border-green-300 bg-green-50' : 'border-slate-200'}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${archiveDownloaded ? 'bg-green-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                          {archiveDownloaded ? <CheckCircle className="h-5 w-5" /> : '1'}
                        </div>
                        <div>
                          <p className="font-medium">Télécharger l'archive complète</p>
                          <p className="text-sm text-slate-500">Contient tous les documents, notes et évaluations</p>
                        </div>
                      </div>
                      <Button
                        variant={archiveDownloaded ? "outline" : "default"}
                        onClick={downloadGraduateArchive}
                        data-testid="graduate-download-archive-btn"
                        className={archiveDownloaded ? "" : "bg-[#8DC63F] hover:bg-[#7AB62F]"}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        {archiveDownloaded ? 'Re-télécharger' : 'Télécharger'}
                      </Button>
                    </div>
                  </div>

                  {/* Étape 2 : Confirmer */}
                  <div className={`p-4 rounded-lg border-2 ${archiveDownloaded ? 'border-slate-200' : 'border-slate-100 bg-slate-50 opacity-60'}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${archiveDownloaded ? 'bg-amber-500 text-white' : 'bg-slate-200 text-slate-400'}`}>
                        2
                      </div>
                      <div>
                        <p className="font-medium">Confirmer la conversion</p>
                        <p className="text-sm text-slate-500">Cette action est définitive</p>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-3 text-red-700">
                    <XCircle className="h-5 w-5" />
                    <p className="font-medium">Le stagiaire n'a pas validé les 3 Blocs de Compétences requis</p>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => { setShowGraduateModal(false); setArchiveDownloaded(false); }} data-testid="graduate-cancel-btn">
              Annuler
            </Button>
            {eligibilityData?.eligible && (
              <Button
                onClick={convertToMoniteur}
                disabled={!archiveDownloaded || converting}
                className="bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="confirm-graduate-btn"
              >
                {converting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Conversion...
                  </>
                ) : (
                  <>
                    <GraduationCap className="h-4 w-4 mr-2" />
                    Convertir en Moniteur
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>

    {/* Modal Liaison Prospect */}
    <Dialog open={showLinkModal} onOpenChange={setShowLinkModal}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Lier à un prospect CRM</DialogTitle>
          <DialogDescription>
            Recherchez un prospect pour lier ses devis et factures à ce stagiaire.
          </DialogDescription>
        </DialogHeader>
        
        {prospectLink && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-800">
                  Actuellement lié à : {prospectLink.prospect?.prenom} {prospectLink.prospect?.nom}
                </p>
                <p className="text-xs text-blue-600">{prospectLink.prospect?.email}</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleUnlinkProspect} className="text-red-600 border-red-300">
                Délier
              </Button>
            </div>
          </div>
        )}
        
        <div className="space-y-3">
          <input
            type="text"
            placeholder="Rechercher par nom, prénom ou email..."
            value={prospectSearch}
            onChange={(e) => handleSearchProspect(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            data-testid="prospect-search-input"
          />
          
          {searchingProspect && <p className="text-sm text-slate-500">Chargement...</p>}
          
          {!searchingProspect && prospectResults.length > 0 && (
            <div className="max-h-60 overflow-y-auto border rounded-lg divide-y">
              {prospectResults.map((p) => (
                <div
                  key={p.prospect_id}
                  className="p-3 hover:bg-blue-50 cursor-pointer transition-colors flex items-center justify-between"
                  onClick={() => handleLinkProspect(p.prospect_id)}
                  data-testid={`prospect-result-${p.prospect_id}`}
                >
                  <div>
                    <p className="font-medium text-slate-800">{p.prenom} {p.nom}</p>
                    <p className="text-xs text-slate-500">{p.email} · {p.centre}</p>
                  </div>
                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                    {p.devis_count} devis
                  </span>
                </div>
              ))}
            </div>
          )}
          
          {!searchingProspect && prospectResults.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-3">Aucun prospect trouvé</p>
          )}
        </div>
      </DialogContent>
    </Dialog>

    <DocumentModal />
    <CarteEtudiantModal 
      open={carteOpen} 
      onClose={setCarteOpen} 
      stagiaire={stagiaire} 
      centreProfile={centreProfile}
    />
  </>
  );
};

// Composant pour l'historique des présences dépliable
const PresenceHistoryCollapsible = ({ presences }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const stats = {
    present: presences.filter(p => p.statut === 'present').length,
    retard: presences.filter(p => p.statut === 'retard').length,
    absent: presences.filter(p => p.statut === 'absent').length
  };
  
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between p-6 hover:bg-slate-50 transition-colors"
        data-testid="toggle-presence-history"
      >
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-[#8DC63F]" />
          <span className="text-xl font-display font-semibold text-[#0F172A]">
            Historique de Présence
          </span>
          <span className="text-sm text-slate-500">({presences.length} entrées)</span>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex gap-2 text-xs">
            <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full">{stats.present} P</span>
            <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full">{stats.retard} R</span>
            <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full">{stats.absent} A</span>
          </div>
          <svg
            className={`h-5 w-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>
      
      {isExpanded && (
        <div className="border-t border-slate-200 p-6">
          {presences.length === 0 ? (
            <p className="text-slate-500 text-center py-4">Aucune présence enregistrée</p>
          ) : (
            <div className="overflow-x-auto max-h-96">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-slate-700">Date</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-700">Période</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-700">Statut</th>
                    <th className="px-4 py-2 text-left font-semibold text-slate-700">Signature</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {presences.sort((a, b) => new Date(b.date) - new Date(a.date)).map((presence, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-4 py-2">{new Date(presence.date).toLocaleDateString('fr-FR')}</td>
                      <td className="px-4 py-2">{presence.periode === 'matin' ? 'Matin' : presence.periode === 'apres_midi' ? 'Après-midi' : 'Journée'}</td>
                      <td className="px-4 py-2">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          presence.statut === 'present' ? 'bg-green-100 text-green-700' :
                          presence.statut === 'retard' ? 'bg-orange-100 text-orange-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {presence.statut === 'present' ? 'Présent' : 
                           presence.statut === 'retard' ? 'Retard' : 'Absent'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        {presence.signature ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-slate-300" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FicheStagiaireCentre;
